import { Easing } from './easing'
import { interpolateColor } from './interpolate-color'
import type {
  AnimationConfig,
  DelayAnimation,
  RepeatAnimation,
  SequenceAnimation,
  SpringAnimation,
  TimingAnimation,
  WebSharedValue,
} from './types'

type AnimationRunner = {
  cancel: () => void
}

let trackingSharedValues: Set<WebSharedValue<unknown>> | null = null

export function runWithTracking<T>(factory: () => T): { result: T; sharedValues: Set<WebSharedValue<unknown>> } {
  const sharedValues = new Set<WebSharedValue<unknown>>()
  trackingSharedValues = sharedValues
  const result = factory()
  trackingSharedValues = null
  return { result, sharedValues }
}

export function isAnimationConfig(value: unknown): value is AnimationConfig {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    typeof (value as AnimationConfig).type === 'string'
  )
}

export function createWebSharedValue<T>(initialValue: T): WebSharedValue<T> {
  let currentValue = initialValue
  const listeners = new Set<() => void>()
  let activeRunner: AnimationRunner | null = null

  const notify = () => {
    listeners.forEach((listener) => {
      listener()
    })
  }

  const sharedValue = {} as WebSharedValue<T>

  const readValue = () => {
    if (trackingSharedValues) {
      trackingSharedValues.add(sharedValue as WebSharedValue<unknown>)
    }

    return currentValue
  }

  const writeValue = (nextValue: T | ((current: T) => T) | AnimationConfig) => {
    if (isAnimationConfig(nextValue)) {
      activeRunner?.cancel()
      activeRunner = runAnimation(sharedValue, currentValue, nextValue, (value) => {
        currentValue = value as T
        notify()
      })
      return
    }

    activeRunner?.cancel()
    activeRunner = null
    currentValue = typeof nextValue === 'function' ? (nextValue as (current: T) => T)(currentValue) : nextValue
    notify()
  }

  Object.defineProperties(sharedValue, {
    value: {
      get: readValue,
      set: writeValue,
    },
  })

  sharedValue.get = readValue
  sharedValue.set = writeValue
  sharedValue._subscribe = (listener) => {
    listeners.add(listener)
    return () => listeners.delete(listener)
  }
  sharedValue._cancelAnimation = () => {
    activeRunner?.cancel()
    activeRunner = null
  }

  return sharedValue
}

export function cancelAnimation(target?: WebSharedValue | WebSharedValue[]): void {
  if (!target) {
    return
  }

  if (Array.isArray(target)) {
    target.forEach((item) => {
      item._cancelAnimation()
    })
    return
  }

  target._cancelAnimation()
}

export function withTiming<T extends number>(
  toValue: T,
  config?: { duration?: number; easing?: (value: number) => number },
  callback?: (finished?: boolean) => void,
): TimingAnimation {
  return {
    type: 'timing',
    toValue,
    config,
    callback,
  }
}

export function withSpring<T extends number>(
  toValue: T,
  config?: { damping?: number; stiffness?: number; mass?: number },
  callback?: (finished?: boolean) => void,
): SpringAnimation {
  return {
    type: 'spring',
    toValue,
    config,
    callback,
  }
}

export function withRepeat(animation: AnimationConfig, repetitions = -1, isReverse = false): RepeatAnimation {
  return {
    type: 'repeat',
    animation,
    repetitions,
    reverse: isReverse,
  }
}

export function withSequence(...animations: AnimationConfig[]): SequenceAnimation {
  return {
    type: 'sequence',
    animations,
  }
}

export function withDelay<_T extends number>(delayMs: number, animation: AnimationConfig): DelayAnimation {
  return {
    type: 'delay',
    delayMs,
    animation,
  }
}

export function interpolate(value: number, inputRange: number[], outputRange: number[]): number {
  const firstInput = inputRange[0] ?? 0
  const lastInput = inputRange[inputRange.length - 1] ?? 1
  const clampedValue = Math.min(lastInput, Math.max(firstInput, value))

  let segmentIndex = 0

  for (let index = 0; index < inputRange.length - 1; index += 1) {
    if (clampedValue <= (inputRange[index + 1] ?? lastInput)) {
      segmentIndex = index
      break
    }
  }

  const startInput = inputRange[segmentIndex] ?? firstInput
  const endInput = inputRange[segmentIndex + 1] ?? lastInput
  const startOutput = outputRange[segmentIndex] ?? outputRange[0] ?? 0
  const endOutput = outputRange[segmentIndex + 1] ?? outputRange[segmentIndex] ?? startOutput
  const progress = endInput === startInput ? 0 : (clampedValue - startInput) / (endInput - startInput)

  return startOutput + progress * (endOutput - startOutput)
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function runAnimation<T>(
  _sharedValue: WebSharedValue<T>,
  fromValue: T,
  animation: AnimationConfig,
  onFrame: (value: T) => void,
  onComplete?: () => void,
): AnimationRunner {
  // Цветовые значения (заливка слайдера, цвет текста): нативный reanimated умеет
  // интерполировать цвета в withTiming/withSpring, числовые раннеры дали бы NaN.
  if (
    (animation.type === 'timing' || animation.type === 'spring') &&
    typeof fromValue === 'string' &&
    typeof animation.toValue === 'string'
  ) {
    return runColorTimingAnimation(fromValue, animation, onFrame as (value: string) => void, onComplete)
  }

  switch (animation.type) {
    case 'timing':
      return runTimingAnimation(fromValue as number, animation, onFrame as (value: number) => void, onComplete)
    case 'spring':
      return runSpringAnimation(fromValue as number, animation, onFrame as (value: number) => void, onComplete)
    case 'repeat':
      return runRepeatAnimation(fromValue as number, animation, onFrame as (value: number) => void, onComplete)
    case 'sequence':
      return runSequenceAnimation(fromValue as number, animation, onFrame as (value: number) => void, onComplete)
    case 'delay':
      return runDelayAnimation(_sharedValue, fromValue, animation, onFrame, onComplete)
    default:
      onComplete?.()
      return { cancel: () => undefined }
  }
}

function runColorTimingAnimation(
  fromValue: string,
  animation: TimingAnimation | SpringAnimation,
  onFrame: (value: string) => void,
  onComplete?: () => void,
): AnimationRunner {
  const duration = (animation.type === 'timing' && animation.config?.duration) || 300
  const easing = (animation.type === 'timing' && animation.config?.easing) || Easing.linear
  const toValue = String(animation.toValue)
  const startedAt = performance.now()
  let frameId = 0
  let cancelled = false

  const tick = (now: number) => {
    if (cancelled) {
      return
    }

    const progress = Math.min(1, (now - startedAt) / duration)
    onFrame(interpolateColor(easing(progress), [0, 1], [fromValue, toValue]))

    if (progress < 1) {
      frameId = requestAnimationFrame(tick)
      return
    }

    animation.callback?.(true)
    onComplete?.()
  }

  frameId = requestAnimationFrame(tick)

  return {
    cancel: () => {
      cancelled = true
      cancelAnimationFrame(frameId)
    },
  }
}

function runTimingAnimation(
  fromValue: number,
  animation: TimingAnimation,
  onFrame: (value: number) => void,
  onComplete?: () => void,
): AnimationRunner {
  const duration = animation.config?.duration ?? 300
  const easing = animation.config?.easing ?? Easing.linear
  const startedAt = performance.now()
  let frameId = 0
  let cancelled = false

  const tick = (now: number) => {
    if (cancelled) {
      return
    }

    const progress = Math.min(1, (now - startedAt) / duration)
    onFrame(fromValue + ((animation.toValue as number) - fromValue) * easing(progress))

    if (progress < 1) {
      frameId = requestAnimationFrame(tick)
      return
    }

    animation.callback?.(true)
    onComplete?.()
  }

  frameId = requestAnimationFrame(tick)

  return {
    cancel: () => {
      cancelled = true
      cancelAnimationFrame(frameId)
    },
  }
}

function runSpringAnimation(
  fromValue: number,
  animation: SpringAnimation,
  onFrame: (value: number) => void,
  onComplete?: () => void,
): AnimationRunner {
  const damping = animation.config?.damping ?? 20
  const stiffness = animation.config?.stiffness ?? 300
  const mass = animation.config?.mass ?? 1
  let velocity = 0
  let currentValue = fromValue
  let frameId = 0
  let cancelled = false

  const tick = () => {
    if (cancelled) {
      return
    }

    const toValue = animation.toValue as number
    const springForce = -stiffness * (currentValue - toValue)
    const dampingForce = -damping * velocity
    const acceleration = (springForce + dampingForce) / mass
    velocity += acceleration / 60
    currentValue += velocity / 60
    onFrame(currentValue)

    if (Math.abs(toValue - currentValue) < 0.001 && Math.abs(velocity) < 0.001) {
      onFrame(toValue)
      animation.callback?.(true)
      onComplete?.()
      return
    }

    frameId = requestAnimationFrame(tick)
  }

  frameId = requestAnimationFrame(tick)

  return {
    cancel: () => {
      cancelled = true
      cancelAnimationFrame(frameId)
    },
  }
}

function runRepeatAnimation(
  fromValue: number,
  animation: RepeatAnimation,
  onFrame: (value: number) => void,
  onComplete?: () => void,
): AnimationRunner {
  const repetitions = animation.repetitions
  const isInfinite = repetitions <= 0
  let repetitionsLeft = isInfinite ? Number.POSITIVE_INFINITY : repetitions
  let currentValue = fromValue
  let activeRunner: AnimationRunner | null = null
  let cancelled = false
  let cycleIndex = 0
  const initialValue = fromValue

  const runCycle = () => {
    if (cancelled || repetitionsLeft <= 0) {
      onComplete?.()
      return
    }

    const baseAnimation = animation.animation
    const isReverseCycle = Boolean(animation.reverse && cycleIndex % 2 === 1)
    let targetAnimation = baseAnimation

    if (baseAnimation.type === 'timing' || baseAnimation.type === 'spring') {
      if (isReverseCycle) {
        targetAnimation = {
          ...baseAnimation,
          toValue: initialValue,
        }
      } else if (cycleIndex > 0) {
        // Как в нативном reanimated: без reverse каждый повтор стартует заново с исходного значения
        currentValue = initialValue
      }
    }

    activeRunner?.cancel()
    activeRunner = runAnimation(
      { set: onFrame } as WebSharedValue<number>,
      currentValue,
      targetAnimation,
      (value) => {
        currentValue = value
        onFrame(value)
      },
      () => {
        cycleIndex += 1

        if (!isInfinite) {
          repetitionsLeft -= 1
        }

        runCycle()
      },
    )
  }

  runCycle()

  return {
    cancel: () => {
      cancelled = true
      activeRunner?.cancel()
    },
  }
}

// Пауза перед анимацией: значение держим прежним, потом запускаем вложенную
function runDelayAnimation<T>(
  sharedValue: WebSharedValue<T>,
  fromValue: T,
  animation: DelayAnimation,
  onFrame: (value: T) => void,
  onComplete?: () => void,
): AnimationRunner {
  let activeRunner: AnimationRunner | null = null
  let cancelled = false

  const timeout = setTimeout(() => {
    if (cancelled) {
      return
    }

    activeRunner = runAnimation(sharedValue, fromValue, animation.animation, onFrame, onComplete)
  }, animation.delayMs)

  return {
    cancel: () => {
      cancelled = true
      clearTimeout(timeout)
      activeRunner?.cancel()
    },
  }
}

function runSequenceAnimation(
  fromValue: number,
  animation: SequenceAnimation,
  onFrame: (value: number) => void,
  onComplete?: () => void,
): AnimationRunner {
  let currentValue = fromValue
  let index = 0
  let activeRunner: AnimationRunner | null = null
  let cancelled = false

  const runNext = () => {
    if (cancelled || index >= animation.animations.length) {
      onComplete?.()
      return
    }

    const nextAnimation = animation.animations[index]
    index += 1

    activeRunner?.cancel()
    activeRunner = runAnimation(
      { set: onFrame } as WebSharedValue<number>,
      currentValue,
      nextAnimation,
      (value) => {
        currentValue = value
        onFrame(value)
      },
      runNext,
    )
  }

  runNext()

  return {
    cancel: () => {
      cancelled = true
      activeRunner?.cancel()
    },
  }
}

type StylePropertyAnimationState = {
  current: number | string
  target?: number | string
  runner: AnimationRunner | null
}

const stylePropertyStates = new WeakMap<object, Map<string, StylePropertyAnimationState>>()

export function processAnimatedStyleValue(owner: object, key: string, value: unknown, onUpdate: () => void): unknown {
  if (!isAnimationConfig(value) || (value.type !== 'timing' && value.type !== 'spring')) {
    return value
  }

  const states = stylePropertyStates.get(owner) ?? new Map<string, StylePropertyAnimationState>()
  stylePropertyStates.set(owner, states)

  const state = states.get(key) ?? { current: value.toValue, target: value.toValue, runner: null }

  if (state.target !== value.toValue) {
    state.target = value.toValue
    state.runner?.cancel()
    state.runner = runAnimation(
      {
        set: (next) => {
          state.current = next as number | string
          onUpdate()
        },
      } as WebSharedValue<number | string>,
      state.current,
      value,
      (next) => {
        state.current = next
        onUpdate()
      },
    )
  }

  states.set(key, state)
  return state.current
}

export function processAnimatedStyleObject(
  owner: object,
  style: Record<string, unknown>,
  onUpdate: () => void,
): Record<string, unknown> {
  const nextStyle: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(style)) {
    if (key === 'transform' && Array.isArray(value)) {
      nextStyle.transform = value.map((item) => {
        if (typeof item !== 'object' || item === null) {
          return item
        }

        const transformEntry = item as Record<string, unknown>
        const transformKey = Object.keys(transformEntry)[0]

        if (!transformKey) {
          return item
        }

        return {
          [transformKey]: processAnimatedStyleValue(
            owner,
            `${key}.${transformKey}`,
            transformEntry[transformKey],
            onUpdate,
          ),
        }
      })
      continue
    }

    nextStyle[key] = processAnimatedStyleValue(owner, key, value, onUpdate)
  }

  return nextStyle
}
